import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Typography,
  Button,
  Input,
} from '@mui/material';
import { useAppSelector } from '../../redux/hooks';
import useUpdateProfile from '../../hooks/queries/useUpdateProfile';
import useCountries from '../../hooks/queries/useCountries';
import useCities from '../../hooks/queries/useCities';
import { ArrowLeftIcon, UserIcon } from '../../components/shared/CustomIcons';
import moment from 'moment';
import * as Yup from 'yup';
import SectionForm from './SectionForm';
import { useUploadProfile } from '../../hooks/queries/useUploadProfile';
import useUserDetails from '../../hooks/queries/useUserDetails';
import { triggerSnackbar } from '../../provider/SnackbarProvider';

const validationSchemas = {
  personal: Yup.object().shape({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    dob: Yup.date()
      .max(new Date(), 'Date of birth must be in the past')
      .required('Date of birth is required'),
    nhs_number: Yup.string().matches(
      /^\d{10}$/,
      'NHS number must be exactly 10 digits'
    ),
    address: Yup.object().shape({
      line1: Yup.string().required('Address line 1 is required'),
      country: Yup.string().required('Country is required'),
      city: Yup.string().required('City is required'),
      postcode: Yup.string().required('Postcode is required'),
    }),
  }),
  contact: Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
    mobile_number: Yup.string().matches(
      /^\d{7,15}$/,
      'Mobile number must be 7-15 digits'
    ),
  }),
};

const ProfileDetailsComponent: React.FC = () => {
  const { id: patientId, profile_url } =
    useAppSelector((state) => state.auth.currentUser) || {};
  const { userDetails } = useUserDetails(patientId || '');
  const { updateProfileDetails } = useUpdateProfile();
  const { mutate: uploadProfilePicture } = useUploadProfile();
  const { data: countries } = useCountries();
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const { data: cities, refetch: refetchCities } = useCities(selectedCountry);
  const [avatarUrl, setAvatarUrl] = useState<string>(profile_url || '');
  const [editMode, setEditMode] = useState({
    personalDetails: false,
    contactDetails: false,
  });
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const handleToggleEdit = useCallback((section: keyof typeof editMode) => {
    setEditMode((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (e) => {
        const selectedFile = e.target.files?.[0];
        const validTypes = ['image/png', 'image/jpeg'];
        if (selectedFile && patientId) {
          if (validTypes.includes(selectedFile.type)) {
            uploadProfilePicture({ file: selectedFile, userId: patientId });
            setAvatarUrl(URL.createObjectURL(selectedFile));
          } else {
            triggerSnackbar({
              message:
                'Invalid file type. Only .png and .jpg formats are allowed.',
              severity: 'error',
            });
          }
        }
      },
      [patientId, uploadProfilePicture]
    );

  const handleClickFileInput = useCallback(() => {
    inputFileRef.current?.click();
  }, []);

  const convertDate = useCallback((inputDate: string): string => {
    return inputDate ? moment.utc(inputDate).format('YYYY-MM-DD') : '';
  }, []);

  const handleCountryChange = useCallback(
    async (countryCode: string) => {
      setSelectedCountry(countryCode);
      await refetchCities();
    },
    [refetchCities]
  );

  const updateProfile = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (values: any, setEditMode: () => void) => {
      const countryCode = countries?.find(
        (country: { code: string }) => country.code === values?.address?.country
      )?.country_code;
      updateProfileDetails(
        { ...values, country_code: countryCode },
        setEditMode
      );
    },
    [countries, updateProfileDetails]
  );

  useEffect(() => {
    setSelectedCountry(userDetails?.address?.country || '');
  }, [userDetails?.address?.country]);

  const personalFormInitialValues = useMemo(
    () => ({
      nhs_number: userDetails?.nhs_number || '',
      first_name: userDetails?.first_name || '',
      last_name: userDetails?.last_name || '',
      dob: convertDate(userDetails?.dob) || '',
      address: userDetails?.address || {
        line1: '',
        line2: '',
        city: '',
        postcode: '',
      },
    }),
    [userDetails, convertDate]
  );

  const contactFormInitialValues = useMemo(
    () => ({
      email: userDetails?.email || '',
      mobile_number: userDetails?.mobile_number || '',
    }),
    [userDetails]
  );

  const countryOptions = useMemo(
    () =>
      countries?.map((country: { code: string; name: string }) => ({
        value: country.code,
        label: country.name,
      })) || [],
    [countries]
  );

  const cityOptions = useMemo(
    () =>
      cities?.map((city: { code: string; name: string }) => ({
        value: city.code,
        label: city.name,
      })) || [],
    [cities]
  );

  return (
    <Box sx={{ bgcolor: 'grey.50', px: 3, py: 8 }} className="flex-1">
      <Box sx={{ mb: 4 }} className="d-flex align-items-center">
        <IconButton
          aria-label="back"
          sx={{ p: 0 }}
          onClick={() => history.back()}
        >
          <ArrowLeftIcon sx={{ fontSize: 40, color: 'text.primary' }} />
        </IconButton>
        <Box
          sx={{ gap: 1.5, ml: -5 }}
          className="d-flex justify-content-center align-items-center flex-1"
        >
          <UserIcon sx={{ color: 'tertiary.main', width: 24, height: 24 }} />
          <Typography variant="h6" sx={{ color: 'grey.900' }}>
            Profile
          </Typography>
        </Box>
      </Box>

      <Box className="d-flex flex-column align-items-center" sx={{ gap: 1.5 }}>
        <Avatar
          src={avatarUrl}
          sx={{
            width: 80,
            height: 80,
            border: '4px solid',
            borderColor: 'primary.main',
          }}
          alt="User Avatar"
        />
        <Button
          onClick={handleClickFileInput}
          sx={{ fontSize: '1rem', color: 'text.secondary' }}
        >
          Change profile picture
          <Input
            type="file"
            onChange={handleFileChange}
            inputRef={inputFileRef}
            sx={{ display: 'none' }}
          />
        </Button>
      </Box>

      <Divider sx={{ borderColor: 'solitude.main', my: 3 }} />

      <SectionForm
        initialValues={personalFormInitialValues}
        validationSchema={validationSchemas.personal}
        fields={[
          {
            name: 'nhs_number',
            label: 'NHS Number',
            type: 'numeric',
            extra: { inputProps: { maxLength: 10 } },
          },
          { name: 'first_name', label: 'First Name', type: 'text' },
          { name: 'last_name', label: 'Last Name', type: 'text' },
          {
            name: 'dob',
            label: 'Date of Birth',
            type: 'date',
            format: 'DD/MM/YYYY',
          },
          { name: 'address.line1', label: 'Address line 1', type: 'text' },
          {
            name: 'address.line2',
            label: 'Address line 2 (optional)',
            type: 'text',
          },
          { name: 'address.postcode', label: 'Postcode', type: 'text' },
          {
            name: 'address.country',
            label: 'Country',
            type: 'select',
            options: countryOptions,
          },
          {
            name: 'address.city',
            label: 'Town or city',
            type: 'select',
            dependentField: 'address.country',
            dependentOptions: cityOptions,
            fetchOptions: async (value: string) => {
              handleCountryChange(value);
            },
          },
        ]}
        section="personalDetails"
        edit={editMode.personalDetails}
        setEditMode={handleToggleEdit}
        updateProfileDetails={updateProfile}
      />

      <Divider sx={{ borderColor: 'solitude.main', my: 3 }} />

      <SectionForm
        initialValues={contactFormInitialValues}
        validationSchema={validationSchemas.contact}
        fields={[
          { name: 'email', label: 'Email', type: 'text', disabled: true },
          {
            name: 'mobile_number',
            label: 'Phone Number',
            type: 'tel',
            disabled: false,
            extra: { prefix: userDetails?.country_code },
          },
        ]}
        section="contactDetails"
        edit={editMode.contactDetails}
        setEditMode={handleToggleEdit}
        updateProfileDetails={updateProfile}
      />
    </Box>
  );
};

export default ProfileDetailsComponent;
